import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  Package,
  Clock,
  User,
  Building,
  FileText,
  DollarSign,
  ExternalLink,
  Edit3,
  X,
  Phone,
  Mail,
  RotateCw,
  Check,
  Tag,
} from "lucide-react";
import { api } from "../../lib/api";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";

const STATUS_BADGES = {
  Pending: { bg: "#fef3c7", text: "#92400e", border: "#fde68a" },
  "Partially Priced": { bg: "#f3e8ff", text: "#7e22ce", border: "#e9d5ff" },
  "In Progress": { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd" },
  "Price Received": { bg: "#dcfce7", text: "#15803d", border: "#bbf7d0" },
  Completed: { bg: "#f1f5f9", text: "#475569", border: "#e2e8f0" },
};

const ITEM_STATUS_BADGES = {
  Pending: { bg: "#fef3c7", text: "#92400e", border: "#fde68a", label: "Pending" },
  "Price Received": { bg: "#dcfce7", text: "#15803d", border: "#bbf7d0", label: "Price Received" },
};

const PRIORITY_BADGES = {
  Urgent: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
  High: { bg: "#ffedd5", text: "#c2410c", border: "#fed7aa" },
  Medium: { bg: "#f1f5f9", text: "#475569", border: "#e2e8f0" },
  Low: { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0" },
};

function formatCurrency(amount, currency = "INR") {
  if (amount === null || amount === undefined || isNaN(amount)) return "—";
  const symbol = currency === "INR" ? "₹" : currency === "USD" ? "$" : currency === "EUR" ? "€" : `${currency} `;
  return `${symbol}${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
}

export default function ProcurementDetail({
  requestId,
  user,
  onBack,
  onOpenLead,
  onOpenClient,
  onChange,
}) {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal State for pricing an individual product item
  const [activeItemModal, setActiveItemModal] = useState(null);
  const [itemPriceForm, setItemPriceForm] = useState({
    price: "",
    currency: "INR",
    pricing_unit: "per unit",
    vendor_name: "",
    vendor_contact: "",
    procurement_notes: "",
    status: "Price Received",
  });
  const [savingPrice, setSavingPrice] = useState(false);
  const [inlinePrices, setInlinePrices] = useState({});
  const [savingBatchPrices, setSavingBatchPrices] = useState(false);
  const [savingRowId, setSavingRowId] = useState("");

  // Status Change State
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const canEnterPrice = isAdmin(user) || canAccessDepartment(user, ["Procurement"]);

  const syncInlinePricesFromRequest = (reqData) => {
    const priceMap = {};
    (reqData.items || []).forEach((itm) => {
      priceMap[itm.item_id] = {
        price: itm.price !== null && itm.price !== undefined ? String(itm.price) : "",
        currency: itm.currency || reqData.currency || "INR",
        pricing_unit: itm.pricing_unit || `per ${itm.unit || "unit"}`,
        vendor_name: itm.vendor_name || "",
        vendor_contact: itm.vendor_contact || "",
        procurement_notes: itm.procurement_notes || "",
      };
    });
    setInlinePrices(priceMap);
  };

  const loadRequest = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api("get", `/procurement/${requestId}`);
      setRequest(res.data);
      syncInlinePricesFromRequest(res.data);
    } catch (err) {
      console.error("Failed to load procurement request:", err);
      toast.error("Could not load procurement request");
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadRequest();
  }, [loadRequest]);

  const handleInlinePriceChange = (itemId, field, value) => {
    setInlinePrices((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
      },
    }));
  };

  // Save single row directly from table input
  const handleSaveSingleRowPrice = async (item) => {
    if (!item || !request) return;
    const rowState = inlinePrices[item.item_id] || {};
    const priceRaw = rowState.price;

    if (priceRaw === "" || isNaN(Number(priceRaw)) || Number(priceRaw) < 0) {
      toast.error(`Please enter a valid price for ${item.product_name}`);
      return;
    }

    setSavingRowId(item.item_id);
    try {
      const payload = {
        price: parseFloat(priceRaw),
        currency: rowState.currency || item.currency || "INR",
        pricing_unit: rowState.pricing_unit || `per ${item.unit || "unit"}`,
        vendor_name: (rowState.vendor_name || "").trim(),
        vendor_contact: (rowState.vendor_contact || "").trim(),
        procurement_notes: (rowState.procurement_notes || "").trim(),
        status: "Price Received",
      };

      const res = await api(
        "put",
        `/procurement/${request.request_id}/items/${item.item_id}/price`,
        payload
      );

      toast.success(`Price saved for ${item.product_name}`);
      setRequest(res.data);
      syncInlinePricesFromRequest(res.data);
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save item price");
    } finally {
      setSavingRowId("");
    }
  };

  // Save / submit prices for ALL products together
  const handleSaveAllPrices = async () => {
    if (!request) return;
    const itemsList = request.items || [];
    if (itemsList.length === 0) return;

    setSavingBatchPrices(true);
    try {
      const batchItems = itemsList.map((itm) => {
        const rowState = inlinePrices[itm.item_id] || {};
        const pRaw = rowState.price;
        const hasPrice = pRaw !== "" && pRaw !== undefined && !isNaN(Number(pRaw)) && Number(pRaw) >= 0;

        return {
          item_id: itm.item_id,
          price: hasPrice ? parseFloat(pRaw) : null,
          currency: rowState.currency || itm.currency || "INR",
          pricing_unit: rowState.pricing_unit || `per ${itm.unit || "unit"}`,
          vendor_name: (rowState.vendor_name || "").trim(),
          vendor_contact: (rowState.vendor_contact || "").trim(),
          procurement_notes: (rowState.procurement_notes || "").trim(),
          status: hasPrice ? "Price Received" : "Pending",
        };
      });

      const res = await api("put", `/procurement/${request.request_id}/batch-prices`, {
        items: batchItems,
      });

      const pricedCount = batchItems.filter((it) => it.status === "Price Received").length;
      if (pricedCount === batchItems.length) {
        toast.success(`All ${batchItems.length} product prices submitted successfully!`);
      } else if (pricedCount > 0) {
        toast.success(`Prices saved! ${pricedCount} of ${batchItems.length} products priced (Partially Priced).`);
      } else {
        toast.info("Prices updated.");
      }

      setRequest(res.data);
      syncInlinePricesFromRequest(res.data);
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit prices");
    } finally {
      setSavingBatchPrices(false);
    }
  };

  // Open modal for a specific product item
  const handleOpenItemPriceModal = (item) => {
    setActiveItemModal(item);
    const rowState = inlinePrices[item.item_id] || {};
    setItemPriceForm({
      price: rowState.price || (item.price !== null && item.price !== undefined ? String(item.price) : ""),
      currency: rowState.currency || item.currency || request?.currency || "INR",
      pricing_unit: rowState.pricing_unit || item.pricing_unit || `per ${item.unit || "unit"}`,
      vendor_name: rowState.vendor_name || item.vendor_name || "",
      vendor_contact: rowState.vendor_contact || item.vendor_contact || "",
      procurement_notes: rowState.procurement_notes || item.procurement_notes || "",
      status: item.status === "Pending" ? "Price Received" : item.status || "Price Received",
    });
  };

  // Submit price for a single product item via modal
  const handleSaveItemPrice = async (e) => {
    e.preventDefault();
    if (!activeItemModal || !request) return;

    if (itemPriceForm.price === "" || isNaN(Number(itemPriceForm.price)) || Number(itemPriceForm.price) < 0) {
      toast.error("Please enter a valid price/rate");
      return;
    }

    setSavingPrice(true);
    try {
      const payload = {
        price: parseFloat(itemPriceForm.price),
        currency: itemPriceForm.currency || "INR",
        pricing_unit: itemPriceForm.pricing_unit || `per ${activeItemModal.unit || "unit"}`,
        vendor_name: itemPriceForm.vendor_name.trim(),
        vendor_contact: itemPriceForm.vendor_contact.trim(),
        procurement_notes: itemPriceForm.procurement_notes.trim(),
        status: itemPriceForm.status || "Price Received",
      };

      const res = await api(
        "put",
        `/procurement/${request.request_id}/items/${activeItemModal.item_id}/price`,
        payload
      );

      toast.success(`Price saved for ${activeItemModal.product_name}`);
      setRequest(res.data);
      syncInlinePricesFromRequest(res.data);
      setActiveItemModal(null);
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update item price");
    } finally {
      setSavingPrice(false);
    }
  };

  // Mark Request Completed or Change Status
  const handleUpdateOverallStatus = async (newStatus) => {
    if (!request) return;
    setUpdatingStatus(true);
    try {
      const res = await api("put", `/procurement/${request.request_id}`, { status: newStatus });
      toast.success(`Request marked as ${newStatus}`);
      setRequest(res.data);
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update request status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const items = useMemo(() => request?.items || [], [request]);
  const totalItemsCount = items.length || 1;
  const pricedItemsCount = useMemo(
    () => items.filter((it) => it.status === "Price Received" || (it.price !== null && it.price !== undefined)).length,
    [items]
  );
  const pendingItemsCount = Math.max(0, totalItemsCount - pricedItemsCount);

  // Check validation and unsaved price changes for Save All Prices
  const { hasValidationErrors, hasUnsavedPrices } = useMemo(() => {
    let errors = false;
    let unsaved = false;

    items.forEach((itm) => {
      const row = inlinePrices[itm.item_id] || {};
      const p = row.price;
      if (p !== "" && p !== undefined) {
        const num = Number(p);
        if (isNaN(num) || num < 0) {
          errors = true;
        } else {
          const savedStr = itm.price !== null && itm.price !== undefined ? String(itm.price) : "";
          if (String(p) !== savedStr) {
            unsaved = true;
          }
        }
      }
    });

    return {
      hasValidationErrors: errors,
      hasUnsavedPrices: unsaved,
    };
  }, [items, inlinePrices]);

  if (loading && !request) {
    return (
      <div className="content" data-testid="procurement-detail-loading">
        <div className="loading-screen">Loading procurement request…</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="content" data-testid="procurement-detail-not-found">
        <button className="back-btn" data-testid="procurement-detail-back-btn" onClick={onBack}>
          <ArrowLeft size={14} /> Back to Procurement
        </button>
        <div className="panel" style={{ padding: 40, textAlign: "center", marginTop: 20 }}>
          <h3>Procurement Request Not Found</h3>
          <p className="muted">This request may have been removed or deleted.</p>
        </div>
      </div>
    );
  }

  const statusBadge = STATUS_BADGES[request.status] || STATUS_BADGES.Pending;
  const priorityBadge = PRIORITY_BADGES[request.priority] || PRIORITY_BADGES.Medium;

  return (
    <div className="content detail-content" data-testid="procurement-detail-page">
      {/* Back Button */}
      <button
        className="back-btn"
        data-testid="procurement-detail-back-btn"
        onClick={onBack}
        style={{ marginBottom: 16 }}
      >
        <ArrowLeft size={14} /> Back to Procurement
      </button>

      {/* Simplified Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--navy)" }}>
            {request.company_name}
          </h1>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              fontSize: 12.5,
              color: "#64748b",
              marginTop: 4,
            }}
          >
            <span>
              Request ID:{" "}
              <strong style={{ color: "#0f172a", fontFamily: "monospace" }}>
                {request.request_id}
              </strong>
            </span>
            <span>•</span>
            <span>
              <strong style={{ color: "#0f172a" }}>{totalItemsCount}</strong>{" "}
              {totalItemsCount === 1 ? "Product" : "Products"} Requested
            </span>
            <span>•</span>
            <span>Created {formatDate(request.created_at)}</span>
            {request.required_by && (
              <>
                <span>•</span>
                <span>
                  Required by <strong style={{ color: "#0f172a" }}>{formatDate(request.required_by)}</strong>
                </span>
              </>
            )}
            <span>•</span>
            <span>
              Priority:{" "}
              <strong style={{ color: priorityBadge.text }}>
                {request.priority || "Medium"}
              </strong>
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Overall Pricing Status Badge */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "4px 12px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              background: statusBadge.bg,
              color: statusBadge.text,
              border: `1px solid ${statusBadge.border || "transparent"}`,
            }}
            data-testid="procurement-status-badge"
          >
            {request.status || "Pending"}
          </span>

          {/* Mark Complete Action Button if Priced and not yet completed */}
          {canEnterPrice && request.status !== "Completed" && pricedItemsCount === totalItemsCount && (
            <button
              type="button"
              className="outline-btn compact"
              onClick={() => handleUpdateOverallStatus("Completed")}
              disabled={updatingStatus}
              data-testid="mark-completed-btn"
              style={{
                fontSize: 12,
                padding: "4px 12px",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                color: "#15803d",
                borderColor: "#bbf7d0",
                background: "#f0fdf4",
                borderRadius: 6,
                fontWeight: 600,
              }}
            >
              <Check size={13} /> Mark Completed
            </button>
          )}
        </div>
      </div>

      {/* Simplified Request Information Strip */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: "14px 18px",
          marginBottom: 20,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 16,
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
        }}
      >
        {/* 1. Client / Lead */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>
            Client / Lead
          </div>
          <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 13, marginBottom: 2 }}>
            {request.company_name}
          </div>
          <div>
            {request.lead_id ? (
              onOpenLead ? (
                <button
                  type="button"
                  onClick={() => onOpenLead(request.lead_id)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "#0284c7",
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: "pointer",
                    textDecoration: "underline",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                  data-testid={`proc-header-lead-link-${request.lead_id}`}
                >
                  Lead #{request.lead_id} <ExternalLink size={10} />
                </button>
              ) : (
                <span style={{ fontSize: 12, color: "#64748b" }}>Lead #{request.lead_id}</span>
              )
            ) : request.client_id ? (
              onOpenClient ? (
                <button
                  type="button"
                  onClick={() => onOpenClient(request.client_id)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "#15803d",
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: "pointer",
                    textDecoration: "underline",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                  data-testid={`proc-header-client-link-${request.client_id}`}
                >
                  Client #{request.client_id} <ExternalLink size={10} />
                </button>
              ) : (
                <span style={{ fontSize: 12, color: "#64748b" }}>Client #{request.client_id}</span>
              )
            ) : (
              <span style={{ fontSize: 12, color: "#94a3b8" }}>—</span>
            )}
          </div>
        </div>

        {/* 2. Contact Information */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>
            Contact
          </div>
          <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 13, marginBottom: 2 }}>
            {request.contact_person || "—"}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", display: "flex", flexDirection: "column", gap: 1 }}>
            {request.email && <span>{request.email}</span>}
            {canViewClientPhone(user) && request.phone && <span>{request.phone}</span>}
            {!request.email && (!canViewClientPhone(user) || !request.phone) && <span style={{ color: "#94a3b8" }}>No direct contact info</span>}
          </div>
        </div>

        {/* 3. Assigned Specialist */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>
            Assigned Specialist
          </div>
          <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 13, marginBottom: 2 }}>
            {request.assigned_to_name || "Procurement Team"}
          </div>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            Requested by: <span style={{ color: "#0f172a", fontWeight: 500 }}>{request.requested_by_name || "Sales"}</span>
          </div>
        </div>

        {/* 4. Pricing Progress */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>
            Pricing Progress
          </div>
          <div style={{ fontWeight: 700, color: pricedItemsCount === totalItemsCount ? "#15803d" : "#0f172a", fontSize: 13.5, marginBottom: 4 }}>
            {pricedItemsCount} of {totalItemsCount} products priced
          </div>
          <div style={{ fontSize: 11.5, color: pendingItemsCount > 0 ? "#b45309" : "#15803d", fontWeight: 500, marginBottom: 6 }}>
            {pendingItemsCount > 0 ? `${pendingItemsCount} awaiting price` : "All products priced"}
          </div>
          {/* Subtle Mini Progress Bar */}
          <div style={{ width: "100%", height: 5, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
            <div
              style={{
                width: `${Math.round((pricedItemsCount / totalItemsCount) * 100)}%`,
                height: "100%",
                background: pricedItemsCount === totalItemsCount ? "#16a34a" : "#2563eb",
                borderRadius: 3,
                transition: "width 0.3s ease",
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Section: Products & Pricing Table */}
      <section
        className="panel"
        style={{
          padding: 0,
          overflow: "hidden",
          marginBottom: 24,
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "#ffffff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.03)",
        }}
        data-testid="procurement-products-pricing-section"
      >
        {/* Table Panel Header */}
        <div
          style={{
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid var(--border)",
            background: "#ffffff",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>
              Products &amp; Pricing
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#64748b" }}>
              Enter supplier rates per unit. Prices automatically calculate totals and sync to lead orders.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {hasValidationErrors && (
              <span style={{ fontSize: 12, color: "#b91c1c", fontWeight: 500 }}>
                Invalid price values entered (must be ≥ 0)
              </span>
            )}
            {canEnterPrice && (
              <button
                type="button"
                className="primary-btn compact"
                onClick={handleSaveAllPrices}
                disabled={savingBatchPrices || hasValidationErrors || !hasUnsavedPrices}
                data-testid="procurement-save-all-prices-btn"
                title={
                  hasValidationErrors
                    ? "Correct invalid values before saving"
                    : !hasUnsavedPrices
                    ? "No unsaved price changes to submit"
                    : "Save all entered prices together"
                }
                style={{
                  fontSize: 12.5,
                  height: 32,
                  padding: "0 16px",
                  background: hasUnsavedPrices && !hasValidationErrors
                    ? "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)"
                    : "#e2e8f0",
                  borderColor: hasUnsavedPrices && !hasValidationErrors ? "#1d4ed8" : "#cbd5e1",
                  cursor: hasUnsavedPrices && !hasValidationErrors ? "pointer" : "not-allowed",
                  borderRadius: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontWeight: 600,
                  color: hasUnsavedPrices && !hasValidationErrors ? "#ffffff" : "#94a3b8",
                  boxShadow: hasUnsavedPrices && !hasValidationErrors ? "0 1px 3px rgba(37,99,235,0.25)" : "none",
                }}
              >
                <Check size={14} />
                {savingBatchPrices ? "Saving Prices..." : "Save All Prices"}
              </button>
            )}
          </div>
        </div>

        {/* Products Table */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }} data-testid="procurement-items-table">
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid var(--border)", textAlign: "left" }}>
                <th style={{ padding: "12px 16px", fontWeight: 600, color: "#475569", fontSize: 12, minWidth: 260 }}>
                  Product &amp; Description
                </th>
                <th style={{ padding: "12px 14px", fontWeight: 600, color: "#475569", fontSize: 12, width: 90, textAlign: "right" }}>
                  Quantity
                </th>
                <th style={{ padding: "12px 14px", fontWeight: 600, color: "#475569", fontSize: 12, width: 75 }}>
                  Unit
                </th>
                <th style={{ padding: "12px 14px", fontWeight: 600, color: "#475569", fontSize: 12, width: 170 }}>
                  Price per Unit
                </th>
                <th style={{ padding: "12px 14px", fontWeight: 600, color: "#475569", fontSize: 12, width: 140, textAlign: "right" }}>
                  Total Price
                </th>
                <th style={{ padding: "12px 14px", fontWeight: 600, color: "#475569", fontSize: 12, width: 130, textAlign: "center" }}>
                  Pricing Status
                </th>
                <th style={{ padding: "12px 16px", fontWeight: 600, color: "#475569", fontSize: 12, width: 110, textAlign: "right" }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const isItemPriced = item.price !== null && item.price !== undefined && item.status === "Price Received";
                const currentInline = inlinePrices[item.item_id] || {};
                const currentPriceVal = currentInline.price !== undefined ? currentInline.price : (item.price ?? "");
                const isInvalidRate = currentPriceVal !== "" && (isNaN(Number(currentPriceVal)) || Number(currentPriceVal) < 0);
                const hasValidNumber = currentPriceVal !== "" && !isNaN(Number(currentPriceVal)) && Number(currentPriceVal) >= 0;

                return (
                  <tr
                    key={item.item_id || idx}
                    style={{
                      borderBottom: "1px solid var(--border)",
                      background: isItemPriced ? "#fbfdfb" : "#ffffff",
                      transition: "background 0.15s ease",
                    }}
                    data-testid={`procurement-item-row-${item.item_id || idx}`}
                  >
                    {/* 1. Product & Description */}
                    <td style={{ padding: "12px 16px", verticalAlign: "top" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 13.5 }}>
                          {item.product_name}
                        </span>
                        {item.order_id && (
                          <span
                            style={{
                              background: "#f1f5f9",
                              color: "#475569",
                              border: "1px solid #e2e8f0",
                              fontSize: 11,
                              fontWeight: 600,
                              padding: "1px 6px",
                              borderRadius: 4,
                              fontFamily: "monospace",
                            }}
                            title={`Linked Lead/Client Order: ${item.order_id}`}
                          >
                            Order: {item.order_id}
                          </span>
                        )}
                      </div>
                      {item.description ? (
                        <div style={{ fontSize: 12, color: "#475569", marginTop: 3, lineHeight: 1.4 }}>
                          {item.description}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>
                          No description provided
                        </div>
                      )}
                      {item.additional_notes && item.additional_notes !== item.description && (
                        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2, fontStyle: "italic" }}>
                          Notes: {item.additional_notes}
                        </div>
                      )}
                      {item.procurement_notes && (
                        <div style={{ fontSize: 11, color: "#059669", marginTop: 3 }}>
                          💬 Note: {item.procurement_notes}
                        </div>
                      )}
                    </td>

                    {/* 2. Quantity */}
                    <td style={{ padding: "12px 14px", fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap", textAlign: "right", verticalAlign: "top" }}>
                      {(item.quantity || 1).toLocaleString()}
                    </td>

                    {/* 3. Unit */}
                    <td style={{ padding: "12px 14px", color: "#64748b", whiteSpace: "nowrap", verticalAlign: "top" }}>
                      {item.unit || "pcs"}
                    </td>

                    {/* 4. Price per Unit */}
                    <td style={{ padding: "12px 14px", verticalAlign: "top" }}>
                      {canEnterPrice ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>₹</span>
                            <input
                              type="number"
                              step="any"
                              min="0"
                              placeholder="0.00"
                              value={currentPriceVal}
                              onChange={(e) => handleInlinePriceChange(item.item_id, "price", e.target.value)}
                              style={{
                                width: 95,
                                height: 32,
                                padding: "0 8px",
                                fontSize: 13,
                                fontWeight: 600,
                                border: isInvalidRate
                                  ? "1.5px solid #ef4444"
                                  : isItemPriced
                                  ? "1px solid #86efac"
                                  : "1px solid #cbd5e1",
                                background: isInvalidRate
                                  ? "#fef2f2"
                                  : isItemPriced
                                  ? "#f0fdf4"
                                  : "#ffffff",
                                borderRadius: 6,
                                color: isInvalidRate ? "#b91c1c" : isItemPriced ? "#15803d" : "#0f172a",
                                outline: "none",
                              }}
                              data-testid={`proc-item-price-input-${item.item_id || idx}`}
                            />
                            <span style={{ fontSize: 11.5, color: "#64748b", whiteSpace: "nowrap" }}>
                              /{item.unit || "unit"}
                            </span>
                          </div>
                          {item.vendor_name && (
                            <div style={{ fontSize: 10.5, color: "#0369a1", marginTop: 2 }}>
                              Supplier: {item.vendor_name}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ verticalAlign: "top" }}>
                          {isItemPriced ? (
                            <span style={{ fontWeight: 600, color: "#15803d", fontSize: 13 }}>
                              {formatCurrency(item.price, item.currency || "INR")}
                              <span style={{ fontSize: 11, fontWeight: 400, color: "#64748b", marginLeft: 3 }}>
                                /{item.pricing_unit || item.unit || "unit"}
                              </span>
                            </span>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>
                              Awaiting rate
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* 5. Total Price (Dedicated Column) */}
                    <td style={{ padding: "12px 14px", textAlign: "right", verticalAlign: "top" }}>
                      {hasValidNumber && Number(currentPriceVal) > 0 ? (
                        <span style={{ fontWeight: 600, color: isItemPriced ? "#15803d" : "#0f172a", fontSize: 13 }}>
                          ₹{(Number(currentPriceVal) * (item.quantity || 1)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>—</span>
                      )}
                    </td>

                    {/* 6. Pricing Status */}
                    <td style={{ padding: "12px 14px", textAlign: "center", whiteSpace: "nowrap", verticalAlign: "top" }}>
                      {isItemPriced ? (
                        <span
                          style={{
                            display: "inline-block",
                            fontSize: 11.5,
                            fontWeight: 600,
                            padding: "3px 9px",
                            borderRadius: 12,
                            background: "#f0fdf4",
                            color: "#166534",
                            border: "1px solid #bbf7d0",
                          }}
                        >
                          ✓ Price Received
                        </span>
                      ) : (
                        <span
                          style={{
                            display: "inline-block",
                            fontSize: 11.5,
                            fontWeight: 600,
                            padding: "3px 9px",
                            borderRadius: 12,
                            background: "#fefce8",
                            color: "#854d0e",
                            border: "1px solid #fef08a",
                          }}
                        >
                          ⏳ Awaiting Price
                        </span>
                      )}
                    </td>

                    {/* 7. Action */}
                    <td style={{ padding: "12px 16px", textAlign: "right", whiteSpace: "nowrap", verticalAlign: "top" }}>
                      {canEnterPrice && (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className="outline-btn compact"
                            onClick={() => handleSaveSingleRowPrice(item)}
                            disabled={savingRowId === item.item_id || isInvalidRate || currentPriceVal === ""}
                            title={isItemPriced ? "Update price" : "Save price"}
                            data-testid={`save-row-price-btn-${item.item_id || idx}`}
                            style={{
                              fontSize: 11.5,
                              height: 28,
                              padding: "0 10px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              fontWeight: 600,
                              borderRadius: 5,
                              color: isItemPriced ? "#15803d" : "#2563eb",
                              borderColor: isItemPriced ? "#bbf7d0" : "#bfdbfe",
                              background: isItemPriced ? "#f0fdf4" : "#eff6ff",
                              cursor: isInvalidRate || currentPriceVal === "" ? "not-allowed" : "pointer",
                            }}
                          >
                            <Check size={12} />
                            <span>{savingRowId === item.item_id ? "..." : isItemPriced ? "Update" : "Save"}</span>
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => handleOpenItemPriceModal(item)}
                            data-testid={`enter-item-price-btn-${item.item_id || idx}`}
                            title="Edit full vendor details & notes"
                            style={{
                              height: 28,
                              width: 28,
                              padding: 0,
                              borderRadius: 5,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#64748b",
                            }}
                          >
                            <Edit3 size={13} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Enter / Update Price Modal for Individual Product Item */}
      {activeItemModal && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10000 }}
          data-testid="item-price-modal-backdrop"
        >
          <div
            className="modal"
            style={{ maxWidth: 500, width: "95%", padding: "20px 22px" }}
            data-testid="item-price-modal"
          >
            {/* Modal Header */}
            <div className="modal-head" style={{ marginBottom: 14 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, color: "var(--navy)" }}>
                  {activeItemModal.price !== null ? "Update Product Price" : "Enter Product Price"}
                </h2>
                <div style={{ margin: "2px 0 0", fontSize: 12, color: "var(--muted)" }}>
                  {request.request_id} · {request.company_name}
                </div>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setActiveItemModal(null)}
                disabled={savingPrice}
              >
                <X size={16} />
              </button>
            </div>

            {/* Selected Product Summary Box */}
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: "10px 12px",
                marginBottom: 14,
                fontSize: 12,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ color: "var(--muted)" }}>Product:</span>
                <b style={{ color: "var(--navy)" }}>{activeItemModal.product_name}</b>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ color: "var(--muted)" }}>Quantity:</span>
                <b>
                  {(activeItemModal.quantity || 1).toLocaleString()} {activeItemModal.unit || "units"}
                </b>
              </div>
              {activeItemModal.additional_notes && (
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed var(--border)", fontSize: 11, color: "var(--muted)" }}>
                  <b style={{ color: "var(--navy)" }}>Notes:</b> {activeItemModal.additional_notes}
                </div>
              )}
            </div>

            <form onSubmit={handleSaveItemPrice}>
              {/* Rate & Currency Row */}
              <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                <div className="dispatch-field" style={{ margin: 0 }}>
                  <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11 }}>
                    Price / Rate <span style={{ color: "var(--red)" }}>*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className="dispatch-control"
                    placeholder="0.00"
                    value={itemPriceForm.price}
                    onChange={(e) => setItemPriceForm({ ...itemPriceForm, price: e.target.value })}
                    required
                    style={{ height: 34, fontSize: 12 }}
                    data-testid="item-price-input"
                    autoFocus
                  />
                </div>
                <div className="dispatch-field" style={{ margin: 0 }}>
                  <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11 }}>
                    Currency
                  </label>
                  <select
                    className="dispatch-control"
                    value={itemPriceForm.currency}
                    onChange={(e) => setItemPriceForm({ ...itemPriceForm, currency: e.target.value })}
                    style={{ height: 34, fontSize: 12 }}
                  >
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
                <div className="dispatch-field" style={{ margin: 0 }}>
                  <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11 }}>
                    Pricing Unit
                  </label>
                  <input
                    type="text"
                    className="dispatch-control"
                    placeholder={`per ${activeItemModal.unit || "unit"}`}
                    value={itemPriceForm.pricing_unit}
                    onChange={(e) => setItemPriceForm({ ...itemPriceForm, pricing_unit: e.target.value })}
                    style={{ height: 34, fontSize: 12 }}
                  />
                </div>
              </div>

              {/* Total Calculation Preview */}
              {itemPriceForm.price && !isNaN(Number(itemPriceForm.price)) && (
                <div
                  style={{
                    background: "#ecfdf5",
                    border: "1px solid #bbf7d0",
                    borderRadius: 4,
                    padding: "6px 10px",
                    marginBottom: 12,
                    fontSize: 11.5,
                    color: "#065f46",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    Total Estimated ({(activeItemModal.quantity || 1).toLocaleString()} × {formatCurrency(itemPriceForm.price, itemPriceForm.currency)}):
                  </span>
                  <strong style={{ fontSize: 13 }}>
                    {formatCurrency(Number(itemPriceForm.price) * Number(activeItemModal.quantity || 1), itemPriceForm.currency)}
                  </strong>
                </div>
              )}

              {/* Vendor Info Row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                <div className="dispatch-field" style={{ margin: 0 }}>
                  <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11 }}>
                    Vendor Name (Optional)
                  </label>
                  <input
                    type="text"
                    className="dispatch-control"
                    placeholder="Supplier name"
                    value={itemPriceForm.vendor_name}
                    onChange={(e) => setItemPriceForm({ ...itemPriceForm, vendor_name: e.target.value })}
                    style={{ height: 34, fontSize: 12 }}
                    data-testid="item-price-vendor-input"
                  />
                </div>
                <div className="dispatch-field" style={{ margin: 0 }}>
                  <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11 }}>
                    Vendor Contact (Optional)
                  </label>
                  <input
                    type="text"
                    className="dispatch-control"
                    placeholder="Phone / email"
                    value={itemPriceForm.vendor_contact}
                    onChange={(e) => setItemPriceForm({ ...itemPriceForm, vendor_contact: e.target.value })}
                    style={{ height: 34, fontSize: 12 }}
                  />
                </div>
              </div>

              {/* Procurement Remarks / Lead Time */}
              <div className="dispatch-field" style={{ marginBottom: 16 }}>
                <label className="dispatch-label" style={{ fontWeight: 600, fontSize: 11 }}>
                  Procurement Remarks / Lead Time (Optional)
                </label>
                <textarea
                  className="dispatch-control"
                  rows={2}
                  placeholder="Lead time, MOQ, freight terms, etc."
                  value={itemPriceForm.procurement_notes}
                  onChange={(e) => setItemPriceForm({ ...itemPriceForm, procurement_notes: e.target.value })}
                  style={{ fontSize: 12, height: "auto", minHeight: 60, resize: "vertical" }}
                />
              </div>

              {/* Modal Actions */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button
                  type="button"
                  className="outline-btn compact"
                  onClick={() => setActiveItemModal(null)}
                  disabled={savingPrice}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-btn compact"
                  disabled={savingPrice}
                  data-testid="save-item-price-btn"
                  style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
                >
                  <Check size={13} />
                  {savingPrice ? "Saving…" : "Save Product Price"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
